import type { Countries, TrackedTranslation } from '@ps/web-shared-components';
import type { SubmissionErrors } from 'final-form';
import React from 'react';
import type { FormRenderProps } from 'react-final-form';
import { useSelector } from 'react-redux';

import { styles as sharedStyles } from '@swf/account';
import createForm from '@swf/components/Forms/Form';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import { select } from '@swf/core/store';
import countries from '@swf/generated/countries.json';

import type { AddressFormValues } from '../useAddressUpdate';

import { TXTCLI_Apply__forChanges } from './AddressFormLayout.messages.json';

interface Props {
  submitButtonText?: TrackedTranslation;
  children: (
    formProps: FormRenderProps<AddressFormValues, Partial<AddressFormValues>>,
    submitButton: React.ReactNode
  ) => React.ReactNode;
  onSubmit: (values: AddressFormValues) => Promise<SubmissionErrors | undefined>;
  initialData?: AddressFormValues;
  submitButtonBlock?: boolean;
  className?: string;
  optionalInitialData?: Partial<AddressFormValues>;
}

const Form = createForm<AddressFormValues>();

const AddressFormLayout: React.FC<Props> = ({
  submitButtonText = TXTCLI_Apply__forChanges,
  children,
  onSubmit,
  initialData,
  submitButtonBlock = true,
  className,
  optionalInitialData
}) => {
  const country = useSelector(select.account.getCountry) as string;
  const streetAddress = useSelector(select.account.getAddress);
  const buildingNumber = useSelector(select.account.getBuildingNumber);
  const streetName = useSelector(select.account.getStreetName);
  const city = useSelector(select.account.getCity);
  const zip = useSelector(select.account.getZipCode);
  const state = useSelector(select.account.getAddressState) as string;
  const orientationNumber = useSelector(select.account.getOrientationNumber);
  const district = useSelector(select.account.getDistrict);
  const partOfMunicipality = useSelector(select.account.getPartOfMunicipality);

  const initialCountry = (countries as Countries).find(({ value }) => value === country)?.text;

  const initialFormValues: AddressFormValues = {
    streetAddress,
    streetName,
    buildingNumber,
    city,
    zip: zip || '',
    state,
    country: initialCountry || '',
    orientationNumber,
    district,
    partOfMunicipality,
    ...optionalInitialData
  };

  return (
    <Form
      className={className}
      onSubmit={onSubmit}
      initialValues={initialData || initialFormValues}
      render={(formProps) => (
        <>
          {children(
            formProps,
            <StarsButton
              block={submitButtonBlock}
              loading={formProps.submitting}
              disabled={
                formProps.pristine || (formProps.hasSubmitErrors && !formProps.dirtySinceLastSubmit)
              }
              type="submit"
              className={sharedStyles.submit}
              translation={submitButtonText}
            />
          )}
        </>
      )}
    />
  );
};

export default AddressFormLayout;
